import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Mail, Eye, EyeOff, Image, AlertCircle } from "lucide-react";
import {
  ENVIRONMENT_LABEL,
  getApiUrl,
  getApiConfig,
  nextEnvironment,
  setApiEnvironment as saveApiEnvironment,
  type ApiEnvironment,
} from "../config/api";
import { saveSessionTokens } from "../config/authFetch";

interface LoginScreenProps {
  onLogin: () => void;
}

interface LoginResponse {
  status: string;
  message: string;
  data?: {
    accessToken: string;
    refreshToken?: string;
    expiresIn?: number;
    userData?: {
      uuid?: string;
      email?: string;
      firstName?: string;
      lastName?: string;
      role?: number;
    };
  };
}

const ENV_DOT: Record<ApiEnvironment, string> = {
  development: "bg-yellow-500",
  production: "bg-green-500",
  local: "bg-sky-500",
};

const LoginScreen: React.FC<LoginScreenProps> = ({ onLogin }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [apiEnvironment, setApiEnvironment] = useState<ApiEnvironment>(
    getApiConfig().environment
  );

  useEffect(() => {
    // Sync with current API environment
    setApiEnvironment(getApiConfig().environment);
  }, []);

  const toggleApiEnvironment = () => {
    const newEnv = nextEnvironment(apiEnvironment);
    setApiEnvironment(newEnv); // Update local state
    saveApiEnvironment(newEnv); // Save to localStorage via config function
    // Reload to apply changes
    globalThis.location.reload();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      console.log(
        "Attempting login to:",
        getApiUrl("/api/auth/v1.0.0/admin/login")
      );

      const response = await fetch(getApiUrl("/api/auth/v1.0.0/admin/login"), {
        method: "POST",
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
        }),
      });

      console.log("Response status:", response.status);
      console.log("Response ok:", response.ok);

      // Try to parse the response
      const result: LoginResponse = await response.json();
      console.log("API Response:", result);

      // Check if login was successful
      if (response.ok && result.status === "success") {
        // Store auth info - handle cases where data might be structured differently
        if (result.data) {
          const { accessToken, refreshToken, userData } = result.data;
          const fullName = [userData?.firstName, userData?.lastName]
            .filter(Boolean)
            .join(" ")
            .trim();
          localStorage.setItem("isAuthenticated", "true");
          saveSessionTokens(accessToken || "", refreshToken || "");
          localStorage.setItem("userEmail", userData?.email || formData.email);
          localStorage.setItem("userId", userData?.uuid || "");
          if (fullName) {
            localStorage.setItem("userName", fullName);
          }
          localStorage.setItem("loginTime", new Date().toISOString());

          console.log("Login successful, redirecting...");
          onLogin();
          navigate("/");
        } else {
          // Success but no data - still allow login
          console.log("Success but no data object, allowing login anyway");
          localStorage.setItem("isAuthenticated", "true");
          localStorage.setItem("userEmail", formData.email);
          localStorage.setItem("loginTime", new Date().toISOString());

          onLogin();
          navigate("/");
        }
      } else {
        // Login failed
        console.error("Login failed:", result);
        setError(
          result.message || "Invalid email or password. Please try again."
        );
      }
    } catch (err) {
      console.error("Login error (caught):", err);
      setError(
        "Failed to connect to server. Please check your connection and try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error when user starts typing
    if (error) setError("");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-white opacity-10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-white opacity-10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-white opacity-5 rounded-full blur-3xl"></div>
      </div>

      {/* API Environment Toggle - Top Right */}
      <div className="absolute top-6 right-6 z-20">
        <button
          onClick={toggleApiEnvironment}
          className="flex items-center gap-2 px-4 py-2 bg-white/90 backdrop-blur-sm rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 border border-white/20"
        >
          <div className="flex items-center gap-2">
            <div
              className={`w-2 h-2 rounded-full ${ENV_DOT[apiEnvironment]} animate-pulse`}
            ></div>
            <span className="text-sm font-semibold text-gray-700">
              {ENVIRONMENT_LABEL[apiEnvironment].short}
            </span>
          </div>
          <div className="text-xs text-gray-500">
            Switch to {ENVIRONMENT_LABEL[nextEnvironment(apiEnvironment)].short}
          </div>
        </button>
      </div>

      {/* Login Card */}
      <div className="relative z-10 w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-3xl shadow-2xl mb-4 transform hover:scale-110 transition-transform duration-300">
            <Image className="w-10 h-10 text-indigo-600" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-2 drop-shadow-lg">
            AI Poster
          </h1>
          <p className="text-white/80 text-lg">Admin Dashboard</p>
        </div>

        {/* Login Form Card */}
        <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border border-white/20">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Welcome Back!
            </h2>
            <p className="text-gray-600">
              Sign in to access your admin dashboard
            </p>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3 animate-in slide-in-from-top-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">{error}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email Input */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all duration-200 hover:border-gray-400"
                  placeholder="Enter your email"
                  required
                />
              </div>
            </div>

            {/* Password Input */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) =>
                    handleInputChange("password", e.target.value)
                  }
                  className="w-full pl-11 pr-12 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all duration-200 hover:border-gray-400"
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3.5 px-4 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Signing in...</span>
                </>
              ) : (
                <span>Sign In</span>
              )}
            </button>
          </form>

          {/* Server Info */}
          <div className="mt-6 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div
                  className={`w-2 h-2 rounded-full ${ENV_DOT[apiEnvironment]} animate-pulse`}
                ></div>
                <p className="text-xs font-semibold text-indigo-900">
                  Connected to:{" "}
                  <span className="font-mono">
                    {ENVIRONMENT_LABEL[apiEnvironment].long}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-white/80 text-sm">
            © 2025 AI Poster. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
